import { useEffect, useRef, useState } from "preact/hooks";
import { initialState, type WidgetState } from "./state";
import { Step1Address } from "./steps/Step1Address";
import { Step2Contact } from "./steps/Step2Contact";
import { Step3Result } from "./steps/Step3Result";

/** What every step component receives. */
export type StepProps = {
  state: WidgetState;
  update: (patch: Partial<WidgetState>) => void;
};

/** postMessage type the embed loader listens for. Must match public/loader.js. */
const RESIZE_MESSAGE = "ks-dac:resize";

/** The three-step widget. Renders its own chrome standalone, the host page's in embed mode. */
export function App() {
  const [state, setState] = useState<WidgetState>(initialState);
  const update = (patch: Partial<WidgetState>) => setState((s) => ({ ...s, ...patch }));

  const widgetRef = useRef<HTMLElement>(null);
  const mounted = useRef(false);

  // Embedded on the marketing site (loader.js appends ?embed=1): render just the
  // form, because the host page already supplies the Kasselman header/footer.
  // Same flag the analytics context and the lead's `source.embed` read.
  const embed =
    typeof window !== "undefined" && new URLSearchParams(window.location.search).has("embed");

  // Focus management. On each step change (not the initial mount) move focus to
  // the new step's heading, so a keyboard user does not get dropped back at the
  // top of the document and a screen reader announces the screen they landed on.
  // preventScroll keeps the embedding host page from jumping. tabindex=-1 makes
  // the heading a scripted focus target without adding it to the tab order.
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    const heading = widgetRef.current?.querySelector<HTMLElement>("h2");
    if (heading) {
      heading.setAttribute("tabindex", "-1");
      heading.focus({ preventScroll: true });
    }
  }, [state.step]);

  // Drop the page background in embed mode so the host page shows through.
  useEffect(() => {
    if (embed) document.body.classList.add("embed-body");
    return () => document.body.classList.remove("embed-body");
  }, [embed]);

  // Auto-resize the embedding iframe.
  //
  // Spec section 9 originally called for a fixed-height modal with the widget
  // scrolling internally and no resize protocol. That does not survive a phone:
  // a fixed iframe short enough to fit a small viewport makes step 2's six
  // controls a nested scroll region inside the page's own scroll, which iOS
  // Safari handles badly (it expands iframes to content height and ignores the
  // internal overflow, so the fixed height is a fiction there anyway). Posting
  // the height lets the host size the frame to the content instead. The marketing
  // modal keeps a min-height, so a browser that never receives the message, or a
  // plain hand-written iframe with no listener, still renders correctly.
  //
  // Guarded on being framed at all: unframed there is no parent to tell, and the
  // guard is also what keeps ResizeObserver (absent in jsdom) out of the tests.
  useEffect(() => {
    if (typeof window === "undefined" || window.parent === window) return;
    if (typeof ResizeObserver === "undefined") return;

    const post = () => {
      // ADR-0019. BODY, NOT documentElement: the latter is floored at the iframe's
      // own viewport height, so it reports the frame back to itself and the
      // protocol becomes a one-way ratchet that can only ever grow. Guarded by a
      // named test in app.test.tsx. `display: flow-root` on body (styles.css)
      // holds the margin containment the old measurement was chosen for.
      const height = Math.ceil(document.body.scrollHeight);
      window.parent.postMessage({ type: RESIZE_MESSAGE, height }, "*");
    };

    post();
    // Observing body, not documentElement, for the same reason. documentElement's
    // box is the viewport, so it does not resize when the content does, and this
    // observer would go quiet exactly when it needs to fire.
    const ro = new ResizeObserver(post);
    ro.observe(document.body);
    return () => ro.disconnect();
  }, []);

  return (
    <div class={embed ? "page embed" : "page"}>
      {!embed && (
        <header class="site-header">
          <a href="https://kasselmansolar.com" target="_blank" rel="noopener noreferrer">
            <img class="site-logo" src="/kasselman-full-logo.png" alt="Kasselman Solar" />
          </a>
          <p class="site-tagline">Reputation forged by results, relationships built on trust.</p>
        </header>
      )}

      <main class="widget" ref={widgetRef}>
        {/* Embed only, and inside the card rather than above it. Standalone
            already carries `.site-header` outside this element, so rendering
            both would stack two logos in one view. Embedded, the host page's own
            header can sit far above the iframe, which is why marketing asked for
            the mark and the red rule inside the embedded region itself. */}
        {embed && (
          <header class="widget-header">
            <img class="widget-logo" src="/kasselman-full-logo.png" alt="Kasselman Solar" />
          </header>
        )}

        {/* REVIEWED COPY, pinned in REVIEWED_COPY in src/test/copyGate.ts against
            the record at docs/legal-review-2026-08-05.md. Editing it here without
            updating the pin fails the copy gate, which is intended (ADR-0001).

            Do not "tidy" these back: "qualify for" is not "get", and "your address
            is in a qualifying area" is not "you're one". Both were narrowed on
            2026-08-05 because the widget checks a tract, not a person, and only one
            of three eligibility routes. "Bigger" is deliberately unquantified; the
            real multiple depends on the customer's utility. The reasoning is in the
            record, question 4. */}
        <h1>
          Some New York homeowners qualify for a bigger battery incentive. See if your address is
          in a qualifying area.
        </h1>

        {state.step === "address" && <Step1Address state={state} update={update} />}
        {state.step === "contact" && <Step2Contact state={state} update={update} />}
        {state.step === "result" && <Step3Result state={state} update={update} />}
      </main>

      {/* ContactAdvisor is NOT here. Each step renders it inside its own nav row,
          in line with Back and Continue, so it reads as one of the actions rather
          than as a stray control floating below the card. It is on every step,
          including embed mode, where the footer below is hidden and it is the only
          way to reach a person from inside the iframe. */}

      {!embed && (
        <footer class="site-footer">
          <img class="footer-logo" src="/kasselman-full-logo.png" alt="Kasselman Solar" />
          <p class="footer-contact">
            <a href="tel:+15184788365">Upstate (518) 478-8365</a>
            <span class="sep" aria-hidden="true">·</span>
            <a href="tel:+18456814088">Hudson Valley (845) 681-4088</a>
            <span class="sep" aria-hidden="true">·</span>
            <a href="mailto:info@kasselmansolar.com">info@kasselmansolar.com</a>
          </p>
          <p class="footer-creds">
            4,000+ solar installations across New York · NYSERDA Platinum Quality Solar Installer
          </p>
        </footer>
      )}
    </div>
  );
}
